import { useEffect, useState } from 'react';
import { chat, type ChatRoom as Room, type RoomState } from '../lib/api.ts';
import { ChatRoom } from './ChatRoom.tsx';
import { ChatHistory } from './ChatHistory.tsx';

/**
 * The staff side: every room this person covers, newest first.
 *
 * A rail rather than a single stream, because someone covering IT at
 * six sites needs to know WHICH site is asking before reading a word.
 * Open rooms sort above closed ones; within each, most recent first.
 */
interface Props {
  /** The site chosen in the header. Empty means every site, which is
      what "All" in that dropdown should mean here too. */
  readonly siteId?: string;
  /** A chat-only account: every site, no filter, no board. */
  readonly assistant?: boolean;
}

export const StaffChat = ({ siteId, assistant = false }: Props) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [active, setActive] = useState<Room | null>(null);
  const [state, setState] = useState<RoomState | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  // Chat has its own scope toggle. The header dropdown must stay on a
  // single site because the board needs one, but someone covering six
  // sites wants every room in one list.
  // An assistant answers every site by definition: they are not
  // attached to one, so a "this site" filter has nothing to mean.
  const [allSites, setAllSites] = useState(assistant);
  const [escalating, setEscalating] = useState(false);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');

  const load = () => {
    chat
      .rooms()
      .then((r) =>
        setRooms(
          siteId && !allSites
            ? r.filter((x) => x.siteId === siteId)
            : r,
        ),
      )
      .catch(() => setRooms([]));
  };

  useEffect(() => {
    load();
    // The rail refreshes even while a room is open, so a second site
    // asking is visible without leaving the conversation.
    const id = window.setInterval(load, 15000);
    return () => window.clearInterval(id);
  }, [siteId, allSites]);

  const enter = async (r: Room) => {
    setActive(r);
    setState(null);
    try {
      setState(await chat.open(r.categoryId, r.siteId));
    } catch {
      setActive(null);
    }
  };


  const makeRequest = async () => {
    if (!state) return;
    try {
      await chat.escalate(state.sessionId, title.trim());
      setNote('Request created from this conversation.');
      setEscalating(false);
      setTitle('');
    } catch {
      setNote('Could not create it. It may already be a ticket.');
    }
  };

  const ago = (iso: string): string => {
    const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
  };

  if (showHistory) {
    return (
      <div>
        <button className="linkish" onClick={() => setShowHistory(false)}>
          Back to conversations
        </button>
        <ChatHistory />
      </div>
    );
  }

  return (
    <div className="chatSplit">
      <nav className="chatRail">
        {!assistant && (
        <div className="segmented">
          <button
            className={allSites ? '' : 'on'}
            onClick={() => setAllSites(false)}
          >
            This site
          </button>
          <button
            className={allSites ? 'on' : ''}
            onClick={() => setAllSites(true)}
          >
            All sites
          </button>
        </div>
        )}

        <button className="linkish" onClick={() => setShowHistory(true)}>
          Past conversations
        </button>
        {rooms.length === 0 && (
          <p className="empty">No conversations yet.</p>
        )}
        {rooms.map((r) => (
          <button
            key={r.id}
            className={
              active?.id === r.id ? 'chatRailItem on' : 'chatRailItem'
            }
            onClick={() => void enter(r)}
          >
            <span className="chatRailTop">
              <span
                className="catTag"
                style={{ background: r.color }}
              />
              <strong>{r.siteName}</strong>
              {r.unread > 0 && <b className="badge">{r.unread}</b>}
            </span>
            <span className="chatRailSub mono">
              {allSites ? '' : ''}{r.categoryName} &middot; {ago(r.lastAt)}
              {r.closedAt ? ' · closed' : ''}
            </span>
          </button>
        ))}
      </nav>

      <div className="chatPane">
        {!active && (
          <p className="empty">Pick a conversation.</p>
        )}
        {active && !state && <p className="empty">Opening...</p>}
        {active && state && (
          <>
            {note && <p className="allClear">{note}</p>}
            <p className="mono chatHead">
              {active.siteName} &middot; {active.categoryName}
            </p>
            {escalating && (
              <div className="resolveBox">
                <p className="mono">What should the ticket be called?</p>
                <input
                  autoFocus
                  value={title}
                  placeholder="Short summary"
                  onChange={(e) => setTitle(e.target.value)}
                />
                <div className="noticeRow">
                  <button
                    className="primary"
                    disabled={title.trim().length < 3}
                    onClick={() => void makeRequest()}
                  >
                    Create ticket
                  </button>
                  <button onClick={() => setEscalating(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <ChatRoom
              key={state.sessionId}
              sessionId={state.sessionId}
              initial={state.messages}
              staffPresent={state.staffPresent}
              onEscalate={() => setEscalating(true)}
              onClose={() => {
                void chat.close(state.sessionId).catch(() => undefined);
                setActive(null);
                setState(null);
                load();
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};
